import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box, Button, Typography, Card, CardContent, Avatar
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { teamApi } from '../../services/api';
import type { Team } from '../../models';
import TeamEditDialog from '../../components/Cricket/TeamEditDialog';

const TeamView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Partial<Team>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchTeam = async () => {
      setLoading(true);
      try {
        const res = await teamApi.getById(Number(id));
        setTeam(res.data);
      } catch (e) {
        setTeam(null);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, [id]);

  const handleEdit = () => {
    setEditingTeam(team || {});
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!team) return;
    setSaving(true);
    try {
      await teamApi.update(team.id, editingTeam);
      setTeam({ ...team, ...editingTeam } as Team);
      setEditDialogOpen(false);
    } catch (e) {
      alert('Failed to update team');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}>Loading...</Box>;
  if (!team) return <Box sx={{ p: 3, color: 'error.main' }}>Team not found</Box>;

  return (
    <Box sx={{ width: '100%', p: { xs: 1, md: 3 } }}>
      <Grid container spacing={3}>
        {/* Summary Card */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={team.image || team.flag || undefined} sx={{ width: 64, height: 64 }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>{team.name}</Typography>
                  <Typography variant="body2">Abbreviation: {team.abbreviation || 'N/A'}</Typography>
                  <Typography variant="body2">Long Name: {team.long_name || 'N/A'}</Typography>
                  <Typography variant="body2">D Name: {team.d_name || 'N/A'}</Typography>
                  <Typography variant="body2">C Name: {team.c_name || 'N/A'}</Typography>
                  <Typography variant="body2">MT Name: {team.mt_name || 'N/A'}</Typography>
                  <Typography variant="body2">F Name: {team.f_name || 'N/A'}</Typography>
                  <Typography variant="body2">D ID: {team.d_id || 'N/A'}</Typography>
                  <Typography variant="body2">C ID: {team.c_id || 'N/A'}</Typography>
                  <Typography variant="body2">MT ID: {team.mt_id || 'N/A'}</Typography>
                  <Typography variant="body2">F ID: {team.f_id || 'N/A'}</Typography>
                  <Typography variant="body2">E ID: {team.e_id || 'N/A'}</Typography>
                  <Typography variant="body2">E O ID: {team.e_o_id || 'N/A'}</Typography>
                  <Typography variant="body2">International: {team.is_international ? 'Yes' : 'No'}</Typography>
                  <Typography variant="body2">Test Playing: {team.is_test_playing ? 'Yes' : 'No'}</Typography>
                  <Typography variant="body2">ODI Playing: {team.is_odi_playing ? 'Yes' : 'No'}</Typography>
                  <Typography variant="body2">Active: {team.is_active ? 'Yes' : 'No'}</Typography>
                  <Typography variant="body2">Created: {team.created_at}</Typography>
                  <Typography variant="body2">Updated: {team.updated_at}</Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="outlined" component={Link} to="/teams">Back to Teams</Button>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" color="primary" onClick={handleEdit}>Edit</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TeamEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSave}
        team={editingTeam}
        onTeamChange={setEditingTeam}
        isNew={false}
        loading={saving}
      />
    </Box>
  );
};

export default TeamView; 